import React from 'react';
import { cn } from '@/lib/utils';

export type AX1SignalTone = 'primary' | 'success' | 'warning' | 'critical' | 'info' | 'neutral';
export type AX1SignalSize = 'xs' | 'sm' | 'md' | 'lg';

// Literal classes keep every semantic variant in the production CSS build.
const TONE_CLASS: Record<AX1SignalTone, string> = {
  primary: 'ax-signal-pulse--primary',
  success: 'ax-signal-pulse--success',
  warning: 'ax-signal-pulse--warning',
  critical: 'ax-signal-pulse--critical',
  info: 'ax-signal-pulse--info',
  neutral: 'ax-signal-pulse--neutral',
};
const SIZE_CLASS: Record<AX1SignalSize, string> = {
  xs: 'ax-signal-pulse--xs',
  sm: 'ax-signal-pulse--sm',
  md: 'ax-signal-pulse--md',
  lg: 'ax-signal-pulse--lg',
};

export interface AX1SignalPulseProps extends React.HTMLAttributes<HTMLSpanElement> {
  tone?: AX1SignalTone;
  size?: AX1SignalSize;
  active?: boolean;
  label?: string;
}

/**
 * AX1's branded live-signal indicator.
 *
 * Use animation only for genuinely changing states: recording, an active call,
 * live processing, or a connection attempt. Stable statuses render as a dot.
 * Loading placeholders should continue to use Skeleton instead.
 */
export const AX1SignalPulse = React.forwardRef<HTMLSpanElement, AX1SignalPulseProps>(
  ({ tone = 'primary', size = 'sm', active = false, label, className, ...props }, ref) => (
    <span
      ref={ref}
      className={cn(
        'ax-signal-pulse',
        TONE_CLASS[tone],
        SIZE_CLASS[size],
        active && 'ax-signal-pulse--active',
        className,
      )}
      role={label ? 'status' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      {...props}
    >
      <span className="ax-signal-pulse__halo" aria-hidden="true" />
      <span className="ax-signal-pulse__core" aria-hidden="true" />
    </span>
  ),
);

AX1SignalPulse.displayName = 'AX1SignalPulse';

export default AX1SignalPulse;
